import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  type AuthError,
} from 'firebase/auth'
import { auth, IS_FIREBASE_ENABLED } from '../lib/firebase'
import { api } from '../lib/api'
import { useOffline } from '../hooks/useOffline'

function firebaseResetErrorPt(code: string): string {
  switch (code) {
    case 'auth/expired-action-code':
      return 'Este link de recuperação expirou. Pede um novo email.'
    case 'auth/invalid-action-code':
      return 'Este link de recuperação já não é válido. Pede um novo email.'
    case 'auth/weak-password':
      return 'A nova senha deve ter pelo menos 6 caracteres.'
    case 'auth/network-request-failed':
      return 'Sem ligação à internet. Verifica a tua conexão.'
    default:
      return 'Não foi possível redefinir a senha. Tenta novamente.'
  }
}

export const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams()
  const oobCode = params.get('oobCode') ?? ''
  const seededEmail = params.get('email') ?? ''
  const [email, setEmail] = useState(seededEmail)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const isOffline = useOffline()

  const isMismatch = useMemo(
    () => confirmPassword.length > 0 && confirmPassword !== password,
    [confirmPassword, password],
  )

  useEffect(() => {
    if (!IS_FIREBASE_ENABLED || !auth || !oobCode) {
      setError('Link de recuperação inválido. Volta à entrada e pede um novo email.')
      return
    }

    let cancelled = false
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        if (cancelled) return
        setEmail(verifiedEmail)
        setReady(true)
      })
      .catch((err) => {
        if (cancelled) return
        const ae = err as AuthError
        setError(firebaseResetErrorPt(ae.code))
      })

    return () => { cancelled = true }
  }, [oobCode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!auth || !oobCode || !ready) return
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await confirmPasswordReset(auth, oobCode, password)
      await api.post('/auth/password-reset-complete', { email }).catch(() => {})
      setSuccess(true)
    } catch (err) {
      const ae = err as AuthError
      setError(firebaseResetErrorPt(ae.code))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-5 sm:p-8">
        <div className="text-center mb-8">
          <img
            src="/brand/logo.svg"
            alt="Tutor"
            className="h-12 sm:h-14 mx-auto mb-2"
          />
          <h1 className="text-xl font-semibold text-gray-900">Redefinir senha</h1>
          <p className="text-gray-500 mt-1.5 text-sm">
            Escolhe uma nova senha para continuares a estudar.
          </p>
        </div>

        {success ? (
          <div className="space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-4 rounded-lg text-sm">
              A tua senha foi redefinida com sucesso. Já podes voltar à entrada e continuar com o teu estudo.
            </div>
            <Link
              to={`/login${email ? `?email=${encodeURIComponent(email)}` : ''}`}
              className="block w-full text-center bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Voltar à entrada
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isOffline && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                Para redefinires a senha precisas de ligação à internet.
              </div>
            )}

            {email && (
              <div className="bg-slate-50 border border-slate-200 text-slate-700 px-4 py-3 rounded-lg text-sm">
                Conta: <strong>{email}</strong>
              </div>
            )}

            {error && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError(null)
                }}
                required
                minLength={6}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  if (error) setError(null)
                }}
                required
                minLength={6}
                placeholder="Repete a nova senha"
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm ${
                  isMismatch ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {isMismatch && (
                <p className="text-xs text-red-600 mt-1">As senhas não coincidem.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || isOffline || !ready || !password || isMismatch}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'A redefinir…' : 'Guardar nova senha'}
            </button>

            <p className="text-center text-sm text-gray-500">
              <Link to="/login" className="text-blue-600 font-medium hover:underline">
                Voltar à entrada
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

export default ResetPasswordPage
